import * as Sentry from '@sentry/nextjs';

/**
 * Did React actually throw the page away, or is Sentry's replay detector
 * guessing?
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * MUCKA-WEB-5 has 1,363 events and 94 users. In September it was traced to
 * CLDR 42 spelling `en-GB`'s short September "Sept" on Node and "Sep" on
 * Apple's ICU (CLAUDE.md #41), and `ukDate` fixed that. It is **still firing**:
 * 355 events across 62 users in the week of 22 September, on builds that
 * contain the fix, and now on Chromium/Android rather than the WebKit majority
 * the ICU theory explained.
 *
 * 🚨 AND THERE IS NO REACT ERROR. Not one, anywhere, in ninety days — measured,
 * not assumed. That matters because the plumbing to carry one is all present
 * and demonstrably working:
 *
 *   · Next wires React's `onRecoverableError` to `reportGlobalError`, which is
 *     `window.reportError` — an uncaught error event on window.
 *   · Sentry's `globalHandlersIntegration` catches exactly that, and does:
 *     MUCKA-WEB-9R is a browser exception from an iOS WKWebView tagged
 *     `mechanism: auto.browser.global_handlers.onerror`, on the same platform.
 *   · Nothing filters it. `denyUrls` is extensions, `ignoreErrors` is
 *     ResizeObserver and `inpage.js`, `beforeSend` drops a Meta in-app beacon
 *     and a JSON-LD scraper. There is no client error sample rate.
 *
 * So the only thing claiming a hydration failure is Sentry's replay heuristic,
 * which infers one from a burst of DOM mutations after a navigation. A
 * full-page re-render looks the same to it as a discarded tree.
 *
 * 🚨 And absence of a React error does NOT settle it, which is the trap worth
 * naming: September's fault was real — the replays show all fifteen `<head>`
 * nodes removed and every child of `<body>` re-inserted, 500-900 of them — and
 * it produced no React error either. In PRODUCTION React does not compose the
 * hydration message at all; `react-dom-client.production.js` contains none of
 * the development wording, only minified codes. So "no exception" is consistent
 * with both a real discard and a false alarm, and reading it as either would be
 * a measurement agreeing with whatever you already believed (CLAUDE.md #22).
 *
 * The one thing that DOES separate them is the DOM. That is what this watches.
 *
 * ── What it measures, and why that exact thing ─────────────────────────────
 *
 * A discarded tree has a signature nothing else produces: **every direct child
 * of `<body>` that existed at hydration and that React would remove is
 * removed**. Normal operation never does that. Modals, portals, toasts and the
 * Mucka panel all ADD children and remove their own; a route change swaps what
 * is inside the root, not the root itself.
 *
 * 🚨 "That React would remove" is load-bearing: React leaves SCRIPT, STYLE and
 * stylesheet LINK nodes in place when it discards the document, and a Next
 * body always has scripts in it. Without that clause this could never fire on
 * a real discard — see `sparedByReact` (MUCKA-WEB-AY, 1 Oct 2026).
 *
 * So the set of body's removable children is snapshotted at install and the
 * observer only reports once every one of them is gone. The report carries
 * `firstDifference`: the removed nodes are the server's HTML, what replaced
 * them is the client's render, and the first line where their structures part
 * names the component that disagreed — which is the question every one of
 * these investigations has had to answer by hand. Head removals ride along as
 * corroboration rather than as the trigger, because Next legitimately adds and
 * removes `<style>` and `<link>` nodes there during ordinary navigation and
 * triggering on those would report a working page.
 *
 * This is a measurement of the thing itself, not a proxy for it: it is the same
 * observation #41 made by hand off the replays, made automatically so the next
 * occurrence answers the question instead of starting another investigation.
 *
 * ── What a report means to a user ──────────────────────────────────────────
 *
 * If this fires, they felt it: scroll position lost, focus lost, a visible
 * flash, and any un-committed input in the discarded tree gone. If it stays
 * silent while MUCKA-WEB-5 keeps counting, the detector is the thing that is
 * wrong and the issue can be archived rather than chased.
 *
 * Either answer closes it. That is the whole point of paying for the observer.
 */

/** How long after install to watch. September's discards landed 16-36ms after load. */
const WATCH_MS = 8_000;

/**
 * Reported at most once per page. A discard re-renders everything, and a second
 * report would describe the recovery rather than the fault.
 */
let reported = false;

/**
 * 🚨 Reports through `Sentry.captureMessage`, never `console.error`.
 *
 * `captureConsoleIntegration` is registered in the server and edge configs and
 * is deliberately absent from the client one, so a browser `console.error`
 * reaches nobody — 15 call sites across 9 client modules were written in the
 * prescribed shape and have never once reported (CLAUDE.md #13). A detector
 * that cannot report is worse than no detector, because its silence gets read
 * as good news. `console.warn` stays as the breadcrumb.
 */
function report(detail: {
  headRemoved: number;
  bodyRemoved: number;
  bodyAdded: number;
  msSinceInstall: number;
  firstDifference: FirstDifference | null;
  servedFor: ServedFor;
}): void {
  if (reported) return;
  reported = true;

  // Constant message, explicit fingerprint, everything variable in `extra`.
  // A count or a path in the title would make every page its own issue and
  // split the one number worth reading (CLAUDE.md #13).
  Sentry.captureMessage('[hydration] the page was rebuilt from scratch after load', {
    level: 'warning',
    // A tag, because it is the number to group by: was this HTML rendered for
    // the page it hydrated on at all? See `servedFor`.
    tags: { area: 'hydration', served_for_other_path: detail.servedFor.otherPath },
    fingerprint: ['hydration-tree-discarded'],
    extra: {
      ...detail,
      // The route, not the full URL: an id in the payload is fine, an id in the
      // grouping is not, and this is the field somebody will group by next.
      pathname: window.location.pathname,
      ...hydrationConditions(),
      meaning: 'Every child of <body> present at hydration that React would remove '
        + '(it spares scripts, styles and stylesheets) was removed. '
        + 'That is a discarded tree, not a re-render: the user lost scroll '
        + 'position, focus and any uncommitted input. Corroborated by the head '
        + 'removal count — September\'s discards took all 15 head nodes with them.',
    },
  });
  console.warn('[hydration] tree discarded after load', detail);
}

/**
 * Which page the server rendered this HTML for, against the page it hydrated on.
 *
 * MUCKA-WEB-A2 / AX, Oct 2026: two of the three shapes of discard on Android
 * were the server's HTML naming a DIFFERENT page from the one in the address
 * bar. On /jobs the server's sidebar had Schedule marked active; on /overview
 * it had Quotes; on a job page the chat panel's suggestions were another
 * page's set (they are chosen per page). Every page derives those from
 * `usePathname()`, so the server really did render another path — and nothing
 * in the report could say whether the document was REQUESTED for that path or
 * the URL changed between the render and hydration.
 *
 * Three readings separate those, none of which renders anything new:
 * - `serverActiveHref`: the sidebar item the server marked active, read off
 *   the nodes snapshotted at install (the server's own HTML).
 * - `requestedPath`: the URL the document was actually fetched for (the
 *   navigation timing entry), against `pathAtInstall` and the report's
 *   `pathname`.
 * - `referrerPath`: where it came from, same-origin only.
 */
export interface ServedFor {
  serverActiveHref: string | null;
  requestedPath: string | null;
  pathAtInstall: string;
  referrerPath: string | null;
  /** `yes` when the server's active item does not own the hydrated path. */
  otherPath: 'yes' | 'no' | 'unknown';
}

function activeHrefIn(nodes: Node[]): string | null {
  for (const node of nodes) {
    if (node.nodeType !== 1) continue;
    const hit = (node as Element).querySelector?.('a.nav-item.active[href]');
    if (hit) return hit.getAttribute('href');
  }
  return null;
}

function pathOf(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url, window.location.href);
    return u.origin === window.location.origin ? `${u.pathname}${u.search}` : null;
  } catch {
    return null;
  }
}

/** Does a sidebar item's href own this path? `/jobs` owns `/jobs/123`. */
export function hrefOwnsPath(href: string, pathname: string): boolean {
  const base = href.split('?')[0].replace(/\/$/, '') || '/';
  return pathname === base || pathname.startsWith(`${base}/`);
}

export function servedFor(originalOrder: Node[], pathAtInstall: string): ServedFor {
  const serverActiveHref = activeHrefIn(originalOrder);
  let requestedPath: string | null = null;
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    requestedPath = pathOf(nav?.name);
  } catch { /* unavailable: say so as null */ }
  const pathname = window.location.pathname;
  return {
    serverActiveHref,
    requestedPath,
    pathAtInstall,
    referrerPath: pathOf(document.referrer),
    otherPath: serverActiveHref === null ? 'unknown' : hrefOwnsPath(serverActiveHref, pathname) ? 'no' : 'yes',
  };
}

/**
 * What React leaves behind when it throws the page away.
 *
 * 🚨 A real discard does NOT empty <body>. react-dom clears a document root
 * "sparingly" (`clearContainerSparingly`): SCRIPT, STYLE and stylesheet LINK
 * nodes stay exactly where they were and only everything else is removed.
 * Every Next page has scripts in body — the white-screen watchdog at the top,
 * the `self.__next_f.push` payloads at the bottom — so the first version of
 * this detector, which waited for EVERY original child to go, could never see
 * a React discard at all. The only thing that ever satisfied it was our own
 * watchdog wiping body for its offline notice (MUCKA-WEB-A2), and it stayed
 * silent through all nine MUCKA-WEB-AY exceptions of 29 Sep - 1 Oct 2026.
 *
 * So the hydration set is exactly the nodes React would remove, by React's own
 * rule. The watchdog's wipe still trips it (it removes everything, scripts
 * included), and `offlineNoticeShown` still says when that is what happened.
 */
function sparedByReact(node: Node): boolean {
  if (node.nodeType !== 1) return false;
  const el = node as Element;
  switch (el.nodeName) {
    case 'SCRIPT':
    case 'STYLE':
      return true;
    case 'LINK':
      return (el.getAttribute('rel') ?? '').toLowerCase() === 'stylesheet';
    default:
      return false;
  }
}

/** Enough to cover a phone page several times over; bounded so a huge table cannot bloat the event. */
const SKELETON_CAP = 4000;
/** How much of each side to show either side of the first difference. */
const DIFF_CONTEXT = 8;

/**
 * A short, deliberately weak fingerprint of a text node: 16 bits of FNV-1a.
 *
 * Length alone cannot see the faults these discards are made of — "09:41"
 * against "09:42", "3" against "4", "Sep" against "Sept" is the only one that
 * changed length — so every report used to stop at a structure that matched
 * and say nothing. A hash separates them. Sixteen bits is on purpose: enough
 * that two different strings almost never look alike on one page, few enough
 * that a value cannot be looked up to recover a customer's name from it.
 */
function textHash(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return ((h ^ (h >>> 16)) & 0xffff).toString(16).padStart(4, '0');
}

/**
 * A page reduced to its structure: one line per element (`tag#id.class.class`),
 * indented by depth, with `#text(<length>:<hash>)` for text. Never a word of
 * the text itself — this goes to Sentry, and a ticket subject or a customer's
 * name is exactly what a page like this shows.
 *
 * 🚨 Comments are dropped, React's Suspense markers included. React writes
 * `<!--$-->` / `<!--/$-->` into SERVER HTML only; a client render never
 * produces them. Kept, they made the two sides differ at the first boundary on
 * every page, so every MUCKA-WEB-A2 report said `at: 1` — server `<!--$-->`,
 * client nothing — and named no component at all (7 Oct 2026).
 */
function skeleton(nodes: Node[]): string[] {
  const out: string[] = [];
  const walk = (node: Node, depth: number): void => {
    if (out.length >= SKELETON_CAP) return;
    const indent = '  '.repeat(depth);
    if (node.nodeType === 3) {
      const text = (node.textContent ?? '').trim();
      if (text.length > 0) out.push(`${indent}#text(${text.length}:${textHash(text)})`);
      return;
    }
    if (node.nodeType !== 1 || sparedByReact(node)) return;
    const el = node as Element;
    const id = el.id ? `#${el.id.slice(0, 40)}` : '';
    const cls = (el.getAttribute('class') ?? '').split(/\s+/).filter(Boolean).slice(0, 3)
      .map(c => `.${c.slice(0, 40)}`).join('');
    out.push(`${indent}${el.nodeName.toLowerCase()}${id}${cls}`);
    for (const child of Array.from(el.childNodes)) walk(child, depth + 1);
  };
  for (const node of nodes) walk(node, 0);
  return out;
}

export interface FirstDifference {
  /** Index of the first line that differs. */
  at: number;
  /** The server's ancestors of that line, outermost first. */
  path: string;
  /** What the server had from there on. */
  server: string[];
  /** What the client rendered from there on. */
  client: string[];
  serverLines: number;
  clientLines: number;
}

/**
 * Where the HTML the server sent and the tree the client drew first disagree.
 *
 * The removed nodes ARE the server's HTML — React detaches them intact — and
 * body's children after the discard are the client's render, so this needs no
 * snapshot taken in advance. Exported for tests.
 */
export function firstDifference(serverNodes: Node[], clientNodes: Node[]): FirstDifference | null {
  const server = skeleton(serverNodes);
  const client = skeleton(clientNodes);
  let at = 0;
  while (at < server.length && at < client.length && server[at] === client[at]) at += 1;
  if (at === server.length && at === client.length) return null;

  const depthOf = (line: string) => line.length - line.trimStart().length;
  const ancestors: string[] = [];
  const reference = server[at] ?? client[at] ?? '';
  let depth = depthOf(reference);
  for (let i = Math.min(at, server.length) - 1; i >= 0 && depth > 0; i -= 1) {
    const d = depthOf(server[i]);
    if (d < depth) {
      ancestors.unshift(server[i].trim());
      depth = d;
    }
  }

  return {
    at,
    path: ['body', ...ancestors].join(' > '),
    server: server.slice(at, at + DIFF_CONTEXT),
    client: client.slice(at, at + DIFF_CONTEXT),
    serverLines: server.length,
    clientLines: client.length,
  };
}

/**
 * The two conditions that decided every discard we have pinned so far, so the
 * next report says which one it is instead of opening another investigation.
 *
 *  · `timeZone` — the device's zone. The server renders in UTC and formats in
 *    London; a component that reads the device's zone while rendering
 *    disagrees with it, and Sentry's own `culture` context showed one
 *    customer's engineers on Europe/Istanbul and Europe/Paris clocks on the events they
 *    raised (26 Sep 2026).
 *  · `navigationType` / `transferSize` / `swControlled` — whether the HTML was
 *    fresh. `transferSize: 0` on a `reload` or `back_forward` means it came out
 *    of a cache (the service worker serves its stored copy when the network
 *    fails), and a page rendered hours ago disagrees with anything computed
 *    from the clock now. Hunter & Hargreaves' replay on 24 Sep was exactly
 *    that. 🚨 The converse does not hold: with `swControlled` true, a non-zero
 *    `transferSize` has not been shown to mean the network answered. Two
 *    MUCKA-WEB-AX discards (6 and 7 Oct) reported 184-241KB and may still have
 *    been stored copies. The server trace id is a better witness: two reports
 *    sharing one were the same stored document (docs/sentry-mucka-web-ax.md).
 *
 * Each read is guarded: a detector that throws while reporting reports nothing.
 */
export function hydrationConditions(): Record<string, string | number | boolean | null> {
  const out: Record<string, string | number | boolean | null> = {
    timeZone: null,
    navigationType: null,
    transferSize: null,
    swControlled: null,
    offlineNoticeShown: null,
    offlineNoticeRestored: null,
    responseStatus: null,
  };
  try { out.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null; } catch { /* keep null */ }
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (nav) {
      out.navigationType = nav.type;
      out.transferSize = nav.transferSize;
      out.responseStatus = (nav as PerformanceNavigationTiming & { responseStatus?: number }).responseStatus ?? null;
    }
  } catch { /* keep null */ }
  try { out.swControlled = !!navigator.serviceWorker?.controller; } catch { /* keep null */ }
  // The white-screen watchdog removes every child of <body> when it shows its
  // offline notice, which is exactly this detector's trigger. Say so, so a
  // report is never read as React discarding the tree when it was our own
  // inline script (MUCKA-WEB-A2, 23-27 Sep 2026).
  try { out.offlineNoticeShown = !!(window as unknown as { __muckaOfflineShown?: unknown }).__muckaOfflineShown; } catch { /* keep null */ }
  // And whether the bundle then arrived and put the server's page back before
  // hydrating (MUCKA-WEB-B3). Shown-and-restored is the healthy late arrival.
  try { out.offlineNoticeRestored = !!(window as unknown as { __muckaOfflineRestored?: unknown }).__muckaOfflineRestored; } catch { /* keep null */ }
  return out;
}

/**
 * Next's own client render, which is not hydration at all.
 *
 * When the server cannot produce HTML for a page it sends
 * `<html id="__next_error__">` with an EMPTY body — nothing in it but the
 * hidden metadata `<div>` — and the client builds the whole page with
 * `createRoot` (`next/dist/client/app-index.js`). `createRoot` on the document
 * clears it, which is exactly this detector's trigger, so every such page used
 * to be reported as a discarded tree. It is not one: there was nothing on
 * screen to lose.
 *
 * 🚨 On this deployment EVERY 404 is one of these (checked 2 Oct 2026:
 * `help.mucka.ai/getting-started/set-up-whatsapp` and a made-up `/pay/<id>`
 * both come back `404` with `id="__next_error__"` and an empty body). Six of
 * the eleven MUCKA-WEB-A2 reports since the detector learned to see React
 * discards were that — four dead `/pay` links opened from Windows Chrome in
 * the US, and one dead help link, all rendering the root not-found page.
 */
export function isNextErrorShell(): boolean {
  try { return document.documentElement.id === '__next_error__'; } catch { return false; }
}

/**
 * Was the error shell a DESIGNED answer — a 404, a 403, a redirect — rather
 * than the server failing to render?
 *
 * Status first, where the browser reports it (`responseStatus`; Chromium and
 * Firefox, recent Safari). Otherwise the digest Next writes into the page's
 * own flight data, which is there whatever the engine.
 */
function errorShellWasDesigned(): boolean {
  try {
    const nav = performance.getEntriesByType('navigation')[0] as
      (PerformanceNavigationTiming & { responseStatus?: number }) | undefined;
    const status = nav?.responseStatus;
    if (typeof status === 'number' && status >= 400 && status < 500) return true;
  } catch { /* fall through to the flight data */ }
  try {
    const flight = (self as unknown as { __next_f?: unknown[] }).__next_f;
    if (Array.isArray(flight)) {
      const text = JSON.stringify(flight);
      if (text.includes('NEXT_HTTP_ERROR_FALLBACK;') || text.includes('NEXT_REDIRECT;')) return true;
    }
  } catch { /* undecided reads as a failure, which is the side worth hearing about */ }
  return false;
}

/**
 * An error shell that was NOT a 404/403/redirect: the server could not render
 * a real page, the user looked at a blank screen until the bundle built it,
 * and nothing server-side necessarily said so — MUCKA-WEB-A2's
 * `/voice-receptionist` report on 2 Oct 2026 (Edge, 35KB of flight data, full
 * page built client-side) had no server event on its trace at all. Its own
 * issue, because it is a different fault from a hydration discard and grouping
 * them together is how the 404s hid it.
 */
function reportErrorShell(): void {
  if (reported) return;
  reported = true;
  Sentry.captureMessage('[ssr] the server could not render this page, so the browser built it', {
    level: 'warning',
    tags: { area: 'ssr' },
    fingerprint: ['ssr-error-shell'],
    extra: {
      pathname: window.location.pathname,
      ...hydrationConditions(),
      meaning: 'The document arrived as Next\'s error shell (<html id="__next_error__">, '
        + 'empty body) and was not a 404, 403 or redirect. The page was rendered '
        + 'entirely in the browser: blank until the JavaScript arrived.',
    },
  });
  console.warn('[ssr] error shell rendered in the browser', window.location.pathname);
}

/**
 * Start watching. Safe to call more than once; safe to call before `<body>`
 * exists, and a no-op anywhere there is no DOM.
 *
 * Returns a teardown for tests. Production never calls it — the observer
 * disconnects itself.
 */
export function installHydrationWatch(): () => void {
  if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') {
    return () => {};
  }

  // Not hydration: Next client-renders an error shell from nothing. Classify it
  // once the document has finished arriving (the flight data streams in after
  // this runs) and never watch the DOM — see `isNextErrorShell`.
  if (isNextErrorShell()) {
    const classify = () => { if (!errorShellWasDesigned()) reportErrorShell(); };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', classify, { once: true });
      return () => document.removeEventListener('DOMContentLoaded', classify);
    }
    classify();
    return () => {};
  }

  const start = () => {
    const body = document.body;
    const head = document.head;
    if (!body) return () => {};

    /**
     * The children present at hydration. A discard removes all of them; a
     * modal opening adds one and removes it again, which never empties this
     * set. Held as a Set so ADDED nodes cannot mask a removal by keeping the
     * count up — an earlier shape counted `body.children.length` and would
     * have read a discard-and-rebuild as healthy, because the rebuild puts
     * the count straight back (CLAUDE.md #22: a number that cannot disagree).
     */
    // Only what React would remove — see `sparedByReact` for why the scripts
    // must not be in here.
    const originalOrder = Array.from(body.childNodes).filter(n => !sparedByReact(n));
    const original = new Set<Node>(originalOrder);
    const installedAt = Date.now();
    const pathAtInstall = `${window.location.pathname}${window.location.search}`;
    let headRemoved = 0;
    let bodyRemoved = 0;
    let bodyAdded = 0;

    if (original.size === 0) return () => {};

    const observer = new MutationObserver(records => {
      for (const record of records) {
        if (record.target === head) {
          headRemoved += record.removedNodes.length;
          continue;
        }
        if (record.target !== body) continue;
        bodyAdded += record.addedNodes.length;
        for (const node of Array.from(record.removedNodes)) {
          if (original.delete(node)) bodyRemoved += 1;
        }
      }

      // Every child that was there at hydration is gone. Nothing but a discard
      // does this.
      if (original.size === 0) {
        let diff: FirstDifference | null = null;
        try {
          diff = firstDifference(
            originalOrder,
            Array.from(body.childNodes).filter(n => !sparedByReact(n)),
          );
        } catch { /* a detector that throws while reporting reports nothing */ }
        report({
          headRemoved,
          bodyRemoved,
          bodyAdded,
          msSinceInstall: Date.now() - installedAt,
          firstDifference: diff,
          servedFor: servedFor(originalOrder, pathAtInstall),
        });
        observer.disconnect();
      }
    });

    observer.observe(body, { childList: true });
    if (head) observer.observe(head, { childList: true });

    // Bounded on purpose. A page left open all day would otherwise keep an
    // observer alive for a fault that only ever happens at hydration, and a
    // legitimate teardown hours later is not this bug.
    const timer = window.setTimeout(() => observer.disconnect(), WATCH_MS);

    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
    };
  };

  if (document.body) return start();

  // Installed before <body> exists (the script runs in <head>). Wait for the
  // parser rather than giving up, or this would silently never watch anything.
  let teardown: (() => void) | null = null;
  const onReady = () => { teardown = start(); };
  document.addEventListener('DOMContentLoaded', onReady, { once: true });
  return () => {
    document.removeEventListener('DOMContentLoaded', onReady);
    teardown?.();
  };
}

/** Test seam: forget that a report has already been sent. */
export function resetHydrationWatchForTests(): void {
  reported = false;
}
